<template>
  <div class="grid grid-cols-2 gap-x-4">
    <div data-cy="contractor-company-form-contact-first-name">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.contact.firstName.label')"
        label-for="contactFirstName"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contactFirstName"
        name="contactFirstName"
        id="contactFirstName"
        :placeholder="
          t('subscription.contractorInformation.form.company.contact.firstName.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contact.firstName'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contact.firstName'] &&
          getHelperMessage({ errorMessage: errors['contact.firstName'] })
        "
      />
    </div>

    <div data-cy="contractor-company-form-contact-last-name">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.contact.lastName.label')"
        label-for="contactLastName"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contactLastName"
        name="contactLastName"
        id="contactLastName"
        :placeholder="
          t('subscription.contractorInformation.form.company.contact.lastName.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contact.lastName'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contact.lastName'] &&
          getHelperMessage({ errorMessage: errors['contact.lastName'] })
        "
      />
    </div>

    <div data-cy="contractor-company-form-siret">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.siret.label')"
        label-for="siret"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="siret"
        name="siret"
        id="siret"
        :placeholder="t('subscription.contractorInformation.form.company.siret.placeholder')"
        :input-state="isValidationAttempted && getInputState({ errorMessage: errors.siret })"
        :helper="
          isValidationAttempted && errors.siret && getHelperMessage({ errorMessage: errors.siret })
        "
      />
    </div>

    <div data-cy="contractor-company-form-company-name">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.companyName.label')"
        label-for="companyName"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="companyName"
        name="companyName"
        id="companyName"
        :placeholder="t('subscription.contractorInformation.form.company.companyName.placeholder')"
        :input-state="isValidationAttempted && getInputState({ errorMessage: errors.companyName })"
        :helper="
          isValidationAttempted &&
          errors.companyName &&
          getHelperMessage({ errorMessage: errors.companyName })
        "
      />
    </div>

    <div data-cy="contractor-company-form-code-naf">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.codeNAF.label')"
        label-for="codeNAF"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="codeNAF"
        name="codeNAF"
        id="codeNAF"
        @input="onInputCodeNAF"
        :placeholder="t('subscription.contractorInformation.form.company.codeNAF.placeholder')"
        :input-state="isValidationAttempted && getInputState({ errorMessage: errors.codeNAF })"
        :helper="
          isValidationAttempted &&
          errors.codeNAF &&
          getHelperMessage({ errorMessage: errors.codeNAF })
        "
      />
    </div>

    <div data-cy="contractor-company-form-vat-number">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.intraCommunityVATNumber.label')"
        :optional-text="t('common.forms.optional')"
        label-for="intraCommunityVATNumber"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="intraCommunityVATNumber"
        name="intraCommunityVATNumber"
        id="intraCommunityVATNumber"
        :placeholder="
          t('subscription.contractorInformation.form.company.intraCommunityVATNumber.placeholder')
        "
      />
    </div>
  </div>
  <AtomDivider class="my-12" />

  <AtomText
    :text="t('subscription.contractorInformation.form.contactTitle')"
    font-style="font-semibold"
    font-size="text-lg"
    data-cy="contractor-company-form-contact-title"
  />

  <div class="grid grid-cols-2 gap-x-4">
    <div data-cy="contractor-company-form-contact-email" class="col-span-2">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.contact.email.label')"
        label-for="contactEmail"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contactEmail"
        name="contactEmail"
        id="contactEmail"
        :placeholder="
          t('subscription.contractorInformation.form.company.contact.email.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contact.email'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contact.email'] &&
          getHelperMessage({ errorMessage: errors['contact.email'] })
        "
      />
    </div>

    <div data-cy="contractor-company-form-contact-email-confirm" class="col-span-2">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.contact.emailConfirm.label')"
        label-for="contactEmailConfirm"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contactEmailConfirm"
        name="contactEmailConfirm"
        id="contactEmailConfirm"
        :placeholder="
          t('subscription.contractorInformation.form.company.contact.emailConfirm.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors.contactEmailConfirm })
        "
        :helper="
          isValidationAttempted &&
          errors.contactEmailConfirm &&
          getHelperMessage({ errorMessage: errors.contactEmailConfirm })
        "
      />
    </div>

    <div data-cy="contractor-company-form-contact-phone-number" class="col-span-2">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.company.contact.phoneNumber.label')"
        label-for="contactPhoneNumber"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputPhone
        v-model="contactPhoneNumber"
        name="contactPhoneNumber"
        id="contactPhoneNumber"
        :indicators="indicators"
        :placeholder="
          t('subscription.contractorInformation.form.company.contact.phoneNumber.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contact.phoneNumber'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contact.phoneNumber'] &&
          getHelperMessage({ errorMessage: errors['contact.phoneNumber'] })
        "
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import { useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import type { SubscriptionCompanyContractor } from '@/types/subscription.type';
import { phoneIndicators } from '@/utils/const.util';
import { getErrorToasterConfig } from '@/utils/error.util';
import { formatPhoneNumber } from '@/utils/format.util';
import {
  emailRegex,
  siretRegex,
  phoneRegex,
  codeNafRegex,
  firstNameRegex,
  lastNameRegex,
} from '@/utils/regex.util';

import { EStorageKey } from '@/constants/storateKeys.constants';
import { useSessionStorage } from '@vueuse/core';

const { t } = useI18n();
const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const { getInputState, getHelperMessage } = useInputState();

const { currentSubscription } = storeToRefs(subscriptionStore);

const contractorInformations = useSessionStorage<{ contractorEmail: string }>(
  EStorageKey.CONTRACTOR_INFORMATIONS,
  { contractorEmail: '' }
);

const isValidationAttempted = ref(false);
const indicators = ref(phoneIndicators);

const onInputCodeNAF = () => {
  codeNAF.value = codeNAF.value.toUpperCase();
};

const contractorCompanySchema = yup.object({
  companyName: yup.string().required(t('common.forms.required')),
  siret: yup
    .string()
    .trim()
    .required(t('common.forms.required'))
    .matches(siretRegex, t('subscription.contractorInformation.form.company.siret.format')),
  codeNAF: yup
    .string()
    .trim()
    .required(t('common.forms.required'))
    .matches(codeNafRegex, t('subscription.contractorInformation.form.company.codeNAF.format')),
  intraCommunityVATNumber: yup.string().nullable().optional(),
  contact: yup.object({
    email: yup
      .string()
      .trim()
      .required(t('common.forms.required'))
      .matches(emailRegex, t('common.forms.format.email')),
    firstName: yup
      .string()
      .required(t('common.forms.required'))
      .matches(firstNameRegex, t('common.forms.format.name')),
    lastName: yup
      .string()
      .required(t('common.forms.required'))
      .matches(lastNameRegex, t('common.forms.format.name')),
    phoneNumber: yup
      .string()
      .trim()
      .required(t('common.forms.required'))
      .matches(phoneRegex, t('common.forms.format.phoneNumber')),
  }),
  contactEmailConfirm: yup
    .string()
    .required(t('common.forms.required'))
    .test('email-match', t('common.forms.emailConfirmMismatch'), function (value) {
      return value === this.parent.contact?.email;
    }),
});

type CompanyContractorForm = SubscriptionCompanyContractor & {
  contactEmailConfirm: string;
};

const initialContactEmail =
  currentSubscription.value.companyContractor?.contact.email ||
  currentSubscription.value.individualContractor?.contractor.email ||
  contractorInformations.value.contractorEmail;

const {
  errors,
  defineField,
  validate: validateCompanyContractorForm,
} = useForm<CompanyContractorForm>({
  validationSchema: contractorCompanySchema,
  initialValues: {
    companyName: currentSubscription.value.companyContractor?.companyName,
    siret: currentSubscription.value.companyContractor?.siret,
    codeNAF: currentSubscription.value.companyContractor?.codeNAF,
    intraCommunityVATNumber: currentSubscription.value.companyContractor?.intraCommunityVATNumber,
    contact: {
      email: initialContactEmail,
      firstName:
        currentSubscription.value.companyContractor?.contact.firstName ||
        currentSubscription.value.individualContractor?.contractor.firstName,
      lastName:
        currentSubscription.value.companyContractor?.contact.lastName ||
        currentSubscription.value.individualContractor?.contractor.lastName,
      phoneNumber:
        currentSubscription.value.companyContractor?.contact.phoneNumber?.replace('+33', '') ||
        currentSubscription.value.individualContractor?.contractor.phoneNumber?.replace('+33', ''),
    },
    contactEmailConfirm: initialContactEmail,
  },
});

const [companyName] = defineField('companyName');
const [siret] = defineField('siret');
const [codeNAF] = defineField('codeNAF');
const [intraCommunityVATNumber] = defineField('intraCommunityVATNumber');
const [contactEmail] = defineField('contact.email');
const [contactEmailConfirm] = defineField('contactEmailConfirm');
const [contactFirstName] = defineField('contact.firstName');
const [contactLastName] = defineField('contact.lastName');
const [contactPhoneNumber] = defineField('contact.phoneNumber');

const validateAndSubmit = async (): Promise<boolean> => {
  isValidationAttempted.value = true;
  const { valid: isFormValid } = await validateCompanyContractorForm();

  if (!isFormValid) {
    return false;
  }

  try {
    await subscriptionStore.updateSubscriptionWithCompanyContractor({
      companyName: companyName.value,
      siret: siret.value.trim().replace(/ +/g, ''),
      codeNAF: codeNAF.value.trim(),
      intraCommunityVATNumber: intraCommunityVATNumber.value?.trim() || null,
      contact: {
        email: contactEmail.value,
        firstName: contactFirstName.value,
        lastName: contactLastName.value,
        phoneNumber: formatPhoneNumber(contactPhoneNumber.value),
      },
    });
    return true;
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'contractor-informations',
      defaultMessage: t(
        'subscription.contractorInformation.api.errors.patchSubscriptionContractorCompany'
      ),
    });
    toastSnackBar.addToast(toastConfig);
    return false;
  }
};

defineExpose({ validateAndSubmit });
</script>
